import React, { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import PageSeo from "../components/PageSeo";

export default function Chat() {
  const {
    channels,
    messages,
    sendMessage,
    user,
    createCommunityChannel,
    createRidePlan,
    joinRide,
    joinedRides,
  } = useApp();

  if (!user) return <Navigate to="/login" />;

  const [activeChannel, setActiveChannel] = useState(channels[0]?.id || "general");
  const [input, setInput] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [createMode, setCreateMode] = useState("group");
  const [showChannels, setShowChannels] = useState(false);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 900 : false));
  const [groupDraft, setGroupDraft] = useState({ name: "", desc: "" });
  const [rideDraft, setRideDraft] = useState({
    title: "",
    date: "",
    from: "",
    to: "",
    distance: "",
    bikeReq: "All bikes",
    max: 12,
    desc: "",
  });
  const bottomRef = useRef(null);
  const MOBILE_NAV_OFFSET = 150;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (channels.length && !channels.some((channel) => channel.id === activeChannel)) {
      setActiveChannel(channels[0].id);
    }
  }, [channels, activeChannel]);

  const currentChannel = channels.find((channel) => channel.id === activeChannel);
  const channelMsgs = messages[activeChannel] || [];
  const linkedRide = currentChannel?.rideId || null;
  const rideJoined = linkedRide ? joinedRides.includes(linkedRide) : false;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [channelMsgs]);

  const handleSend = () => {
    if (!input.trim()) return;
    sendMessage(activeChannel, input.trim());
    setInput("");
  };

  const handleCreateGroup = (event) => {
    event.preventDefault();
    if (!groupDraft.name.trim()) return;
    const channel = createCommunityChannel({
      name: groupDraft.name.trim(),
      desc: groupDraft.desc.trim() || "Community riders discussing routes and plans.",
    });
    setActiveChannel(channel.id);
    setShowCreate(false);
    setGroupDraft({ name: "", desc: "" });
  };

  const handleCreateRidePlan = (event) => {
    event.preventDefault();
    if (!rideDraft.title.trim() || !rideDraft.from.trim() || !rideDraft.to.trim() || !rideDraft.date) return;
    const { channel } = createRidePlan({
      ...rideDraft,
      title: rideDraft.title.trim(),
      from: rideDraft.from.trim(),
      to: rideDraft.to.trim(),
      desc: rideDraft.desc.trim(),
      tags: ["Chat Planned", "Weekend Friendly"],
    });
    setActiveChannel(channel.id);
    setShowCreate(false);
    setRideDraft({
      title: "",
      date: "",
      from: "",
      to: "",
      distance: "",
      bikeReq: "All bikes",
      max: 12,
      desc: "",
    });
  };

  const avatarColor = (str) => {
    if (!str || typeof str !== "string") return "#f97316";
    const colors = ["#E84C1E", "#3B82F6", "#22C55E", "#F59E0B", "#A855F7", "#06B6D4"];
    return colors[str.charCodeAt(0) % colors.length];
  };

  const sidebar = (
    <div
      style={{
        width: isMobile ? "85vw" : 300,
        maxWidth: isMobile ? 320 : "none",
        borderRight: "1px solid var(--border)",
        background: "var(--bg2)",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        height: "100%",
      }}
    >
      <div style={{ padding: "16px", borderBottom: "1px solid var(--border)" }}>
        <div style={{ fontFamily: "var(--font-display)", fontSize: 20 }}>COMMUNITY</div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>Create chats and ride plans</div>
        <button
          onClick={() => setShowCreate(true)}
          style={{ marginTop: 12, width: "100%", padding: "10px 12px", borderRadius: "var(--radius)", background: "var(--brand)", color: "#fff", fontSize: 13, fontWeight: 600 }}
        >
          + New Group or Ride
        </button>
      </div>

      <div style={{ flex: 1, padding: 8, overflowY: "auto" }}>
        {channels.map((channel) => (
          <button
            key={channel.id}
            onClick={() => {
              setActiveChannel(channel.id);
              if (isMobile) setShowChannels(false);
            }}
            style={{
              width: "100%",
              textAlign: "left",
              padding: "12px",
              borderRadius: "var(--radius)",
              background: activeChannel === channel.id ? "var(--bg4)" : "transparent",
              border: `1px solid ${activeChannel === channel.id ? "var(--border2)" : "transparent"}`,
              marginBottom: 6,
              color: "var(--text)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 11, color: "var(--brand)", fontWeight: 700 }}>{channel.icon}</span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{channel.name}</span>
            </div>
            <div style={{ fontSize: 11, color: "var(--text3)", lineHeight: 1.4 }}>{channel.desc}</div>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <PageSeo
        title="Rider Community Chat | MotoHub India"
        description="Join MotoHub India community chat channels for ride planning, route discussions, and biker conversations across India."
        path="/chat"
        noindex
      />

      <div style={{ display: "flex", height: isMobile ? "calc(100vh - 170px)" : "calc(100vh - 140px)", minHeight: 540, overflow: "hidden", border: "1px solid var(--border)", borderRadius: 16 }}>
        {!isMobile && sidebar}

        {isMobile && showChannels && (
          <>
            <div onClick={() => setShowChannels(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1300 }} />
            <div style={{ position: "fixed", top: MOBILE_NAV_OFFSET, left: 0, bottom: 0, zIndex: 1301 }}>{sidebar}</div>
          </>
        )}

        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          <div style={{ padding: isMobile ? "12px" : "14px 20px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 12, background: "var(--bg2)" }}>
            {isMobile && (
              <button onClick={() => setShowChannels(true)} style={{ padding: "8px 10px", borderRadius: 8, background: "var(--bg4)", color: "var(--text)" }}>
                Channels
              </button>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{currentChannel?.name}</div>
              <div style={{ fontSize: 12, color: "var(--text3)" }}>{currentChannel?.desc} - {channelMsgs.length} messages</div>
            </div>
            {linkedRide && (
              <button
                onClick={() => !rideJoined && joinRide(linkedRide)}
                style={{
                  marginLeft: "auto",
                  padding: isMobile ? "8px 10px" : "9px 14px",
                  borderRadius: "var(--radius)",
                  background: rideJoined ? "var(--bg4)" : "var(--brand)",
                  color: rideJoined ? "var(--text2)" : "#fff",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {rideJoined ? "Joined" : "Join Ride"}
              </button>
            )}
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? "12px" : "16px 20px", display: "flex", flexDirection: "column", gap: 2 }}>
            {channelMsgs
              .filter(msg => msg && msg.user)
              .map((msg, index) => {
                const prevMsg = channelMsgs[index - 1];
                const showAvatar = !prevMsg || prevMsg.user !== msg.user;
                return (
                  <div key={msg.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", flexDirection: msg.mine ? "row-reverse" : "row", marginTop: showAvatar ? 12 : 2 }}>
                    {showAvatar ? (
                      <div style={{ width: 30, height: 30, borderRadius: "50%", background: msg.mine ? "var(--brand)" : avatarColor(msg.avatar || msg.user?.slice(0,2)?.toUpperCase() || 'MB'), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, color: "#fff", flexShrink: 0 }}>
                        {msg.avatar || msg.user?.slice(0,2)?.toUpperCase() || 'MB'}
                      </div>
                    ) : (
                      <div style={{ width: 30, flexShrink: 0 }} />
                    )}
                    <div style={{ maxWidth: isMobile ? "84%" : "70%" }}>
                      {showAvatar && !msg.mine && <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 4 }}>{msg.user} - {msg.time}</div>}
                      <div style={{ padding: "9px 12px", borderRadius: msg.mine ? "14px 14px 4px 14px" : "14px 14px 14px 4px", background: msg.mine ? "var(--brand)" : "var(--bg3)", border: `1px solid ${msg.mine ? "var(--brand)" : "var(--border)"}`, fontSize: 14, lineHeight: 1.45, color: "var(--text)", overflowWrap: "anywhere" }}>
                        {msg.text}
                      </div>
                    </div>
                  </div>
                );
              })}
            <div ref={bottomRef} />
          </div>

          <div style={{ padding: isMobile ? "10px" : "12px 20px", borderTop: "1px solid var(--border)", background: "var(--bg2)", display: "flex", gap: 8, alignItems: "center" }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              placeholder={`Message ${currentChannel?.name || "channel"}...`}
              style={{ flex: 1 }}
            />
            <button onClick={handleSend} style={{ background: "var(--brand)", color: "#fff", minWidth: 72, height: 42, borderRadius: 10, fontSize: 13, fontWeight: 600 }}>
              Send
            </button>
          </div>
        </div>
      </div>

      {showCreate && (
        <div onClick={() => setShowCreate(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 120 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 520, background: "var(--bg2)", border: "1px solid var(--border2)", borderRadius: "var(--radius-lg)", padding: isMobile ? 16 : 24, maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
              {[{ id: "group", label: "Create Group Chat" }, { id: "ride", label: "Create Ride Plan" }].map((option) => (
                <button
                  key={option.id}
                  onClick={() => setCreateMode(option.id)}
                  style={{ padding: "8px 14px", borderRadius: 999, background: createMode === option.id ? "var(--brand)" : "var(--bg3)", color: createMode === option.id ? "#fff" : "var(--text2)", border: `1px solid ${createMode === option.id ? "var(--brand)" : "var(--border)"}`, fontSize: 13, fontWeight: 600 }}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {createMode === "group" ? (
              <form onSubmit={handleCreateGroup} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <input placeholder="Group name" value={groupDraft.name} onChange={(e) => setGroupDraft((prev) => ({ ...prev, name: e.target.value }))} required />
                <textarea rows={3} placeholder="What is this group about?" value={groupDraft.desc} onChange={(e) => setGroupDraft((prev) => ({ ...prev, desc: e.target.value }))} />
                <button type="submit" style={submitBtn}>Create Group Chat</button>
              </form>
            ) : (
              <form onSubmit={handleCreateRidePlan} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <input placeholder="Ride title" value={rideDraft.title} onChange={(e) => setRideDraft((prev) => ({ ...prev, title: e.target.value }))} required />
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
                  <input placeholder="From" value={rideDraft.from} onChange={(e) => setRideDraft((prev) => ({ ...prev, from: e.target.value }))} required />
                  <input placeholder="To" value={rideDraft.to} onChange={(e) => setRideDraft((prev) => ({ ...prev, to: e.target.value }))} required />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
                  <input type="date" value={rideDraft.date} onChange={(e) => setRideDraft((prev) => ({ ...prev, date: e.target.value }))} required />
                  <input placeholder="Distance (e.g. 180 km)" value={rideDraft.distance} onChange={(e) => setRideDraft((prev) => ({ ...prev, distance: e.target.value }))} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
                  <select value={rideDraft.bikeReq} onChange={(e) => setRideDraft((prev) => ({ ...prev, bikeReq: e.target.value }))}>
                    <option>All bikes</option>
                    <option>150cc+</option>
                    <option>250cc+</option>
                    <option>350cc+</option>
                    <option>500cc+</option>
                  </select>
                  <input type="number" min={2} max={50} placeholder="Max riders" value={rideDraft.max} onChange={(e) => setRideDraft((prev) => ({ ...prev, max: e.target.value }))} />
                </div>
                <textarea rows={3} placeholder="Ride plan notes..." value={rideDraft.desc} onChange={(e) => setRideDraft((prev) => ({ ...prev, desc: e.target.value }))} />
                <button type="submit" style={submitBtn}>Publish Ride Plan</button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const submitBtn = {
  padding: 12,
  borderRadius: "var(--radius)",
  background: "var(--brand)",
  color: "#fff",
  fontWeight: 600,
};
